import { useContext, useEffect } from 'react';
import { AppContext } from '../providers/AppProvider';
import { loadExternalScriptToDOM } from '../helpers/loadExternalScriptToDOM';
import { useSelectedOrganization } from './useSelectedOrganization';
import { useUserData } from './useUserData';
import { useAuth } from './useAuth';
import { isImpersonating } from '../helpers/impersonationHelpers';
import { UserAppRole } from '../models/graphql';

declare global {
  interface Window {
    hj?: CallableFunction;
  }
}

const SCRIPT_CONTENT = (hotjarId: string) =>
  `(function(h,o,t,j,a,r){
  h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
  h._hjSettings={hjid:${hotjarId},hjsv:6};
  a=o.getElementsByTagName('head')[0];
  r=o.createElement('script');r.async=1;
  r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
  a.appendChild(r);
  })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`;

const addUserEventData = (userId: string, organizationId: string | undefined) => {
  if (window.hj) {
    // Tag recordings with u_id - this shows up in Actions as an event.
    window.hj('event', `user_${userId}`);

    // Organization may not be selected yet.
    if (organizationId) {
      // Tag recordings with org id - this shows up in Actions as an event.
      window.hj('event', `org_${organizationId}`);
    }
  }
};

export const useHotjar = () => {
  const { isLoggedIn } = useAuth();
  const { userData } = useUserData();
  const { appConfig } = useContext(AppContext);
  const { selectedOrganization } = useSelectedOrganization();

  useEffect(() => {
    if (appConfig.hotjarApiId) {
      if (!isLoggedIn) {
        loadExternalScriptToDOM(undefined, 'hotjarScript', SCRIPT_CONTENT(appConfig.hotjarApiId));
      } else if (isLoggedIn && userData && !isImpersonating() && userData.appRole === UserAppRole.USER) {
        loadExternalScriptToDOM(undefined, 'hotjarScript', SCRIPT_CONTENT(appConfig.hotjarApiId)).then(() => {
          addUserEventData(userData.id, selectedOrganization?.id);
        });
      }

      // User may select an organization after the script loads, so make sure we update the event data.
      if (isLoggedIn && userData?.id && selectedOrganization?.id) {
        addUserEventData(userData.id, selectedOrganization.id);
      }
    }
  }, [appConfig.hotjarApiId, isLoggedIn, selectedOrganization, userData]);
};
